// Momentum — who's trending up sprint-over-sprint, derived from per-developer
// velocity stats. Ported from qa-live (QA-2286), dark-theme inline styles.

import { useMemo } from 'react'
import { getInitials } from './pulseHelpers.js'
import { colorFromName } from './pulseHelpers.js'
import { AVATAR_PALETTE, getQaTeamMember, QA_TEAM_MEMBERS, PULSE_COLORS } from './pulseConfig.js'

const colorFor = (name) => colorFromName(name, AVATAR_PALETTE)

export default function QaMomentum({ sprints, headerAction }) {
    const { movers, quieter, curLabel, prevLabel } = useMemo(() => {
        const withData = sprints.filter((s) => s.developerStats && s.developerStats.length > 0)
        if (withData.length < 2) {
            return { movers: [], quieter: null, curLabel: '', prevLabel: '' }
        }
        const cur = withData[withData.length - 1]
        const prev = withData[withData.length - 2]

        const statFor = (sprint, email) => sprint.developerStats.find((d) => getQaTeamMember(d.name)?.email === email)

        const deltas = QA_TEAM_MEMBERS.map((m) => {
            const c = statFor(cur, m.email)
            const p = statFor(prev, m.email)
            const curBugs = c?.bugsOpened ?? 0
            const curStories = c?.issueCount ?? 0
            const prevBugs = p?.bugsOpened ?? 0
            const prevStories = p?.issueCount ?? 0
            return {
                name: m.displayName,
                bugsDelta: curBugs - prevBugs,
                storiesDelta: curStories - prevStories,
                activityDelta: curBugs + curStories - (prevBugs + prevStories),
                curBugs,
                curStories,
            }
        })

        const best = (key) => [...deltas].sort((a, b) => b[key] - a[key])[0]

        const topImprover = best('activityDelta')
        const risingDetector = best('bugsDelta')
        const storiesSurge = best('storiesDelta')

        const moverList = []
        if (topImprover && topImprover.activityDelta > 0) {
            moverList.push({
                title: 'Biggest jump',
                emoji: '🚀',
                member: topImprover,
                valueLabel: `+${topImprover.activityDelta} total (bugs + QA stories)`,
            })
        }
        if (risingDetector && risingDetector.bugsDelta > 0 && risingDetector.name !== topImprover?.name) {
            moverList.push({
                title: 'Rising in detection',
                emoji: '🐛',
                member: risingDetector,
                valueLabel: `+${risingDetector.bugsDelta} bugs found`,
            })
        }
        if (
            storiesSurge &&
            storiesSurge.storiesDelta > 0 &&
            storiesSurge.name !== topImprover?.name &&
            storiesSurge.name !== risingDetector?.name
        ) {
            moverList.push({
                title: 'QA stories surge',
                emoji: '📋',
                member: storiesSurge,
                valueLabel: `+${storiesSurge.storiesDelta} QA stories`,
            })
        }

        const quietest = [...deltas].sort((a, b) => a.activityDelta - b.activityDelta)[0]
        const quieterMember = quietest && quietest.activityDelta < 0 ? quietest : null

        return {
            movers: moverList.slice(0, 3),
            quieter: quieterMember,
            curLabel: cur.label,
            prevLabel: prev.label,
        }
    }, [sprints])

    return (
        <div
            style={{
                background: PULSE_COLORS.cardBg,
                borderRadius: '12px',
                padding: '20px',
                border: `1px solid ${PULSE_COLORS.border}`,
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    marginBottom: '16px',
                }}
            >
                <div>
                    <h3 style={{ color: PULSE_COLORS.text, fontWeight: 600, fontSize: '16px', margin: 0 }}>
                        🔥 Momentum
                    </h3>
                    <p style={{ marginTop: '2px', fontSize: '11px', color: PULSE_COLORS.textMuted }}>
                        {curLabel && prevLabel
                            ? `Who's trending up — ${curLabel} vs ${prevLabel}`
                            : "Who's trending up sprint over sprint"}
                    </p>
                </div>
                {headerAction}
            </div>

            {movers.length === 0 ? (
                <p style={{ fontSize: '13px', color: PULSE_COLORS.textMuted }}>
                    Not enough sprint history yet to show movement.
                </p>
            ) : (
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                        gap: '12px',
                    }}
                >
                    {movers.map((mv) => {
                        const color = colorFor(mv.member.name)
                        return (
                            <div
                                key={mv.title}
                                style={{
                                    borderRadius: '8px',
                                    border: '1px solid rgba(16,185,129,0.3)',
                                    background: 'rgba(16,185,129,0.08)',
                                    padding: '12px',
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: '10px',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        color: '#34D399',
                                        marginBottom: '8px',
                                    }}
                                >
                                    {mv.emoji} {mv.title}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span
                                        style={{
                                            width: '36px',
                                            height: '36px',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#fff',
                                            fontSize: '12px',
                                            fontWeight: 700,
                                            flexShrink: 0,
                                            backgroundColor: color,
                                        }}
                                    >
                                        {getInitials(mv.member.name)}
                                    </span>
                                    <div style={{ minWidth: 0 }}>
                                        <div
                                            style={{
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                color: PULSE_COLORS.text,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {mv.member.name.split(' ')[0]}
                                        </div>
                                        <div style={{ fontSize: '11px', fontWeight: 500, color: '#34D399' }}>
                                            ↑ {mv.valueLabel}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}

            {quieter && (
                <div
                    style={{
                        marginTop: '12px',
                        fontSize: '11px',
                        color: '#FBBF24',
                        background: 'rgba(245,158,11,0.1)',
                        border: '1px solid rgba(245,158,11,0.3)',
                        borderRadius: '8px',
                        padding: '8px 12px',
                    }}
                >
                    👀 Quieter this sprint: {quieter.name.split(' ')[0]} ({quieter.activityDelta} vs {prevLabel}) —
                    worth a check-in.
                </div>
            )}
        </div>
    )
}
